import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Target, Users, Award, CheckCircle, ArrowRight } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Reliability',
      description: 'We deliver on our promises. Every load, every time, without exception.',
      color: 'red',
    },
    {
      icon: Target,
      title: 'Precision',
      description: 'Attention to detail in every aspect of our service, from scheduling to delivery.',
      color: 'blue',
    },
    {
      icon: Users,
      title: 'Accountability',
      description: 'We take full responsibility for your cargo from pickup to final delivery.',
      color: 'red',
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Committed to exceeding expectations with professional, quality service.',
      color: 'blue',
    },
  ];

  const commitments = [
    'Clear, honest communication at every step',
    'Professional handling of all cargo',
    'On-time pickup and delivery',
    'Transparent pricing with no hidden fees',
    'Flexible scheduling to meet your needs',
    'Dedicated customer support',
  ];

  return (
    <div className="bg-texture min-h-screen" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-brand-surface border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-6">
              <span className="text-white">ABOUT </span><span style={{ color: '#DC2626' }}>TOP</span><span style={{ color: '#FFFFFF' }}>FLEET</span>
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Built on the principles of reliability, precision, and accountability.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 md:py-32" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
                OUR <span className="text-brand-blue">STORY</span>
              </h2>
              <div className="space-y-6 text-slate-400 text-lg leading-relaxed">
                <p>
                  <span style={{ color: '#DC2626' }}>Top</span><span className="text-white">Fleet</span> Logistics was built in <span className="text-brand-red font-semibold">Augusta, Georgia</span>, with one truck and a commitment to <span className="text-white font-semibold">reliability, precision, and accountability</span>. What began on the road is being built into a company customers can depend on.
                </p>
                <p>
                  Founded by <span className="text-white font-semibold">U.S. Army veteran Richard Dantzler</span>, TopFleet brings a hands-on approach to transportation; focused on professional service, clear communication, and treating every load like it matters.
                </p>
                <p>
                  From our Augusta roots to routes across the country, we're building TopFleet <span className="text-white font-semibold">one load, one relationship, and one mile at a time</span>.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-xl overflow-hidden">
                <img 
                  src="https://customer-assets-0z36b82j.emergentagent.net/job_box-truck-delivery/artifacts/9d3kpwkc_image.png"
                  alt="TopFleet Logistics driver with 26ft box truck at loading dock"
                  className="w-full object-cover aspect-[4/3]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 via-transparent to-transparent" />
              </div>
              <div className="absolute bottom-8 left-8 right-8">
                <p className="text-white font-heading text-2xl">
                  "Driven by Reliability.<br />
                  <span className="text-brand-red">Delivered with Precision.</span>"
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-brand-surface" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-4">
              OUR <span className="text-brand-red">VALUES</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="service-card rounded-xl border border-white/10 bg-brand-navy p-8 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`value-${value.title.toLowerCase()}`}
              >
                <div className={`icon-container ${value.color} mx-auto mb-6`}>
                  <value.icon className="w-7 h-7" />
                </div>
                <h3 className="font-heading text-xl text-white mb-3">{value.title}</h3>
                <p className="text-slate-400 text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-24 md:py-32" data-testid="commitment-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="bg-brand-surface rounded-xl border border-white/10 p-8 md:p-12">
                <h3 className="font-heading text-2xl text-white mb-8">
                  OUR COMMITMENT <span className="text-brand-blue">TO YOU</span>
                </h3>
                <ul className="space-y-4">
                  {commitments.map((commitment, index) => (
                    <li key={`commitment-${commitment.slice(0, 15).replace(/\s+/g, '-')}`} className="flex items-start gap-3">
                      <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${index % 2 === 0 ? 'text-brand-red' : 'text-brand-blue'}`} />
                      <span className="text-slate-300">{commitment}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
                PROFESSIONAL <span className="text-brand-red">SERVICE</span>
              </h2>
              <p className="text-slate-400 text-lg mb-8">
                When you choose <span style={{ color: '#DC2626' }}>Top</span><span className="text-white">Fleet</span> Logistics, you're choosing a partner committed to your success. We treat every delivery as if it were our own, with the same care and attention to detail.
              </p>
              <p className="text-slate-400 text-lg mb-8">
                Our goal is simple: to provide transportation services that you can rely on, every single time. No excuses, no surprises — just professional, efficient service.
              </p>
              <Link 
                to="/contact" 
                className="inline-flex items-center gap-2 text-brand-red font-semibold hover:underline"
                data-testid="about-contact-link"
              >
                Get in Touch
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-brand-surface" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              READY TO <span className="text-brand-blue">WORK TOGETHER?</span>
            </h2>
            <p className="text-xl text-slate-400 mb-10">
              Let's discuss how <span style={{ color: '#DC2626' }}>Top</span><span className="text-white">Fleet</span> Logistics can support your transportation needs.
            </p>
            <Link 
              to="/contact" 
              className="btn-cta text-lg px-10 py-4 inline-block"
              data-testid="about-cta-button"
            >
              Contact Us Today
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
